import React, { useState, useEffect, lazy, Suspense } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Spin, theme } from "antd";
import { setListaCatalogues } from "../redux/Catalogues/actions";
import { secureGetItem } from "../data/utils";

// Lazy loading dei componenti
const GeneralInfoSection = lazy(() => import("../components/Home/GeneralInfoSection"));
const CatalogsSection = lazy(() => import("../components/Home/CatalogsSection"));
const ARShadesLibrarySection = lazy(() => import("../components/Home/ARShadesLibrarySection"));
const SubscriptionsSection = lazy(() => import("../components/Home/SubscriptionsSection"));
const KPISection = lazy(() => import("../components/Home/KPISection"));

// Cache Manager
class HomeCache {
    constructor() {
        this.cache = new Map();
        this.cacheTimeout = 5 * 60 * 1000; // 5 minuti
    }

    set(key, data) {
        this.cache.set(key, {
            data,
            timestamp: Date.now()
        });
    }

    get(key) {
        const cached = this.cache.get(key);
        if (!cached) return null;

        const isExpired = Date.now() - cached.timestamp > this.cacheTimeout;
        if (isExpired) {
            this.cache.delete(key);
            return null;
        }

        return cached.data;
    }

    clear() {
        this.cache.clear();
    }

    has(key) {
        return this.cache.has(key) && !this.isExpired(key);
    }

    isExpired(key) {
        const cached = this.cache.get(key);
        if (!cached) return true;
        return Date.now() - cached.timestamp > this.cacheTimeout;
    }
}

// Singleton cache instance
const homeCache = new HomeCache();

const Home = () => {
    const { token } = theme.useToken();
    const [visibleSections, setVisibleSections] = useState({
        general: true,    // Sempre visibile subito
        catalogs: false,
        library: false,
        subscriptions: false,
        kpi: false
    });
    const [initialLoading, setInitialLoading] = useState(true);
    const [userRole, setUserRole] = useState(null);

    // Redux state
    const rxCatalogues = useSelector((state) => state.catalogues?.listaCatalogues || []);
    const dispatch = useDispatch();

    // Funzione per determinare se l'utente può vedere le sezioni Subscriptions e KPI
    const canViewSubscriptionsAndKPI = (role) => {
        const restrictedRoles = ['Modeller', 'ModellerSupervisor'];
        return !restrictedRoles.includes(role);
    };

    // Carica il ruolo utente e i cataloghi
    useEffect(() => {
        const loadUserData = () => {
            // Recupera il ruolo utente
            const role = secureGetItem("role");
            setUserRole(role);

            // Carica i cataloghi se necessario
            if (rxCatalogues.length === 0) {
                const savedCatalogues = localStorage.getItem("catalogues");
                if (savedCatalogues) {
                    try {
                        const parsedCatalogues = JSON.parse(savedCatalogues);
                        dispatch(setListaCatalogues(parsedCatalogues));
                    } catch (error) {
                        console.error("Error parsing saved catalogues:", error);
                    }
                }
            }
        };

        loadUserData();
        setInitialLoading(false);
    }, [rxCatalogues.length, dispatch]);

    // Lazy loading progressivo delle sezioni
    useEffect(() => {
        if (initialLoading || !userRole) return;

        const loadSectionsProgressively = () => {
            const canViewAdvanced = canViewSubscriptionsAndKPI(userRole);

            // Carica le sezioni con delay progressivo
            const delays = [
                { section: 'catalogs', delay: 500 },
                { section: 'library', delay: 1000 },
                ...(canViewAdvanced ? [
                    { section: 'subscriptions', delay: 1500 },
                    { section: 'kpi', delay: 2000 }
                ] : [])
            ];

            delays.forEach(({ section, delay }) => {
                setTimeout(() => {
                    setVisibleSections(prev => ({
                        ...prev,
                        [section]: true
                    }));
                }, delay);
            });
        };

        loadSectionsProgressively();
    }, [initialLoading, userRole]);

    // Componente per il loading delle sezioni
    const SectionLoader = ({ height = "200px" }) => (
        <div
            style={{
                height,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: token.colorFillQuaternary,
                borderRadius: "0.5rem",
                marginBottom: "2rem"
            }}
        >
            <Spin size="large" />
        </div>
    );

    // Loading iniziale
    if (initialLoading) {
        return (
            <div
                style={{
                    minHeight: "calc(100vh - 200px)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <Spin size="large" />
            </div>
        );
    }

    const canViewAdvanced = canViewSubscriptionsAndKPI(userRole);

    return (
        <div style={{ padding: "0 16px" }}>
            {/* Sezione Informazioni Generali - Sempre visibile */}
            <div style={{ marginBottom: "2rem" }}>
                <Suspense fallback={<SectionLoader height="150px" />}>
                    <GeneralInfoSection />
                </Suspense>
            </div>

            {/* Sezione My Catalogue - Lazy loaded */}
            <div style={{ marginBottom: "2rem" }}>
                {visibleSections.catalogs ? (
                    <Suspense fallback={<SectionLoader height="200px" />}>
                        <CatalogsSection />
                    </Suspense>
                ) : (
                    <SectionLoader height="200px" />
                )}
            </div>

            {/* Sezione ARShades Library - Lazy loaded */}
            <div style={{ marginBottom: "2rem" }}>
                {visibleSections.library ? (
                    <Suspense fallback={<SectionLoader height="200px" />}>
                        <ARShadesLibrarySection />
                    </Suspense>
                ) : (
                    <SectionLoader height="200px" />
                )}
            </div>

            {/* Sezione Subscriptions - Lazy loaded - Solo per ruoli autorizzati */}
            {canViewAdvanced && (
                <div style={{ marginBottom: "2rem" }}>
                    {visibleSections.subscriptions ? (
                        <Suspense fallback={<SectionLoader height="200px" />}>
                            <SubscriptionsSection />
                        </Suspense>
                    ) : (
                        <SectionLoader height="200px" />
                    )}
                </div>
            )}

            {/* Sezione KPI - Lazy loaded - Solo per ruoli autorizzati */}
            {canViewAdvanced && (
                <div style={{ marginBottom: "2rem" }}>
                    {visibleSections.kpi ? (
                        <Suspense fallback={<SectionLoader height="300px" />}>
                            <KPISection />
                        </Suspense>
                    ) : (
                        <SectionLoader height="300px" />
                    )}
                </div>
            )}
        </div>
    );
};

// Esporta anche il cache manager per uso nelle sezioni
export { homeCache };
export default Home;